import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box, Button, TextField, Typography, Paper, Alert, CircularProgress,
  Grid, Checkbox, FormControlLabel,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import AuthShell from "../components/AuthShell";
import { alpha } from "@mui/material/styles";

const NAVY    = "#0F2156";
const CRIMSON = "#B91C2C";

function validateName(val: string, t: (k: string) => string): string | null {
  if (val.trim().length < 2) return t("auth.nameTooShort");
  if (val.trim().length > 20) return t("auth.nameTooLong");
  return null;
}

// Username: 3–20 chars, must start with a letter, only letters/numbers/_/-/.
function validateUsername(val: string, t: (k: string) => string): string | null {
  if (val.length < 3 || val.length > 20) return t("auth.usernameInvalid");
  if (!/^[a-zA-Z]/.test(val)) return t("auth.usernameInvalid");
  if (!/^[a-zA-Z][a-zA-Z0-9_\-.]*$/.test(val)) return t("auth.usernameInvalid");
  return null;
}

function validateEmail(val: string, t: (k: string) => string): string | null {
  if (!val.trim()) return t("auth.emailRequired");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim())) return t("auth.emailInvalid");
  return null;
}

function validatePassword(val: string, t: (k: string) => string): string | null {
  if (val.length < 6) return t("auth.passwordTooShort");
  if (val.length > 30) return t("auth.passwordTooLong");
  return null;
}

export default function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const { t } = useLanguage();

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    password: "",
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear error on edit
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation — top to bottom (first name, last name, username, email, password, terms)
    const firstNameErr = validateName(form.first_name, t);
    if (firstNameErr) { setError(firstNameErr); return; }

    const lastNameErr = validateName(form.last_name, t);
    if (lastNameErr) { setError(lastNameErr); return; }

    const usernameErr = validateUsername(form.username, t);
    if (usernameErr) { setError(usernameErr); return; }

    const emailErr = validateEmail(form.email, t);
    if (emailErr) { setError(emailErr); return; }

    const passwordErr = validatePassword(form.password, t);
    if (passwordErr) { setError(passwordErr); return; }

    if (!agreedToTerms) { setError(t("auth.termsRequired")); return; }

    setLoading(true);
    setError(null);
    try {
      const res = await signup({ ...form, agree_to_terms: true });
      navigate("/verify-email", {
        state: { emailOrUsername: form.email, devCode: res.devCode },
      });
    } catch (err) {
      const msg = (err instanceof Error ? err.message : "").toLowerCase();
      if (msg.includes("username") && msg.includes("taken")) {
        setError(t("auth.usernameTaken"));
      } else if (msg.includes("email") && msg.includes("taken")) {
        setError(t("auth.emailTaken"));
      } else {
        setError(t("auth.createAccount"));
      }
    } finally {
      setLoading(false);
    }
  };

  const headerRight = (
    <Button
      variant="outlined"
      size="small"
      onClick={() => navigate("/login")}
      sx={{
        color: "white",
        borderColor: alpha("#fff", 0.4),
        fontWeight: 600,
        textTransform: "none",
        fontSize: "0.82rem",
        "&:hover": { borderColor: "white", bgcolor: alpha("#fff", 0.08) },
      }}
    >
      {t("auth.login")}
    </Button>
  );

  return (
    <AuthShell rightContent={headerRight}>
      <Box
        sx={{
          minHeight: "calc(100vh - 56px - 60px)",
          background: `linear-gradient(145deg, ${NAVY} 0%, #1a3a7a 45%, ${alpha(CRIMSON, 0.85)} 100%)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
          px: 2,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: alpha("#E8A020", 0.1),
            filter: "blur(80px)",
            pointerEvents: "none",
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 480,
            p: { xs: 3, sm: 4 },
            borderRadius: "20px",
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
            position: "relative", zIndex: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: 3,
              borderRadius: "20px 20px 0 0",
              background: `linear-gradient(90deg, ${CRIMSON} 0%, ${NAVY} 100%)`,
            },
          }}
        >
          <Typography
            variant="h5"
            fontWeight={800}
            mb={2.5}
            textAlign="center"
            sx={{ color: "#1A1A2E", letterSpacing: "-0.02em" }}
          >
            {t("auth.createAccount")}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label={t("auth.firstName")}
                  fullWidth
                  value={form.first_name}
                  onChange={handleChange("first_name")}
                  placeholder={t("auth.firstNamePlaceholder")}
                  required
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t("auth.lastName")}
                  fullWidth
                  value={form.last_name}
                  onChange={handleChange("last_name")}
                  placeholder={t("auth.lastNamePlaceholder")}
                  required
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
            </Grid>

            <TextField
              label={t("auth.username")}
              fullWidth
              margin="normal"
              value={form.username}
              onChange={handleChange("username")}
              placeholder={t("auth.usernamePlaceholder")}
              required
              inputProps={{ maxLength: 20 }}
            />

            <TextField
              label={t("auth.email")}
              type="email"
              fullWidth
              margin="normal"
              value={form.email}
              onChange={handleChange("email")}
              placeholder={t("auth.emailPlaceholder")}
              required
              autoComplete="email"
            />

            <TextField
              label={t("auth.password")}
              type="password"
              fullWidth
              margin="normal"
              value={form.password}
              onChange={handleChange("password")}
              placeholder={t("auth.passwordPlaceholder")}
              required
              autoComplete="new-password"
              inputProps={{ minLength: 6, maxLength: 30 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: "0.82rem" }}>
                  {t("auth.terms")}{" "}
                  <Typography
                    variant="body2"
                    component={Link}
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: "none", fontWeight: 700, color: CRIMSON, fontSize: "0.82rem" }}
                  >
                    {t("auth.viewTerms")}
                  </Typography>
                </Typography>
              }
              sx={{ mt: 1 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 2.5, mb: 1.5, py: 1.4, borderRadius: 12, fontWeight: 700, fontSize: "0.95rem" }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : t("auth.signup")}
            </Button>
          </Box>

          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Typography variant="body2" color="text.secondary" component="span">
              {t("auth.hasAccount")}{" "}
            </Typography>
            <Typography
              variant="body2"
              component={Link}
              to="/login"
              sx={{ textDecoration: "none", fontWeight: 700, color: CRIMSON }}
            >
              {t("auth.login")}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </AuthShell>
  );
}
